import type {
  AnnouncementDto,
  AnnouncementStateDto,
} from '@/api/announcements-admin-client';

// =============================================================================
// announcement-labels.ts — Mapping des états d'annonce (API) → libellés FR + variant de badge.
// =============================================================================
// L'API porte l'enum ASCII `announcement_state` ; l'UI le rend en libellé FR accentué (i18n front). WCAG
// 2.1 AA (ST-NFR-03) : la COULEUR n'est JAMAIS le seul porteur d'information — chaque état rend un LIBELLÉ
// TEXTE ; le variant de badge n'est qu'un renfort visuel. Aucune règle métier : table de traduction pure.
//
// State machine (§5.2.7) : Brouillon → Planifiee (différé) | Modifiable (immédiat, fenêtre ouverte) →
// Verrouillee (fenêtre écoulée, terminal) ; Planifiee → Annulee (terminal).

/** Variants de badge du DS employés pour les états (cf. components/ui/badge.tsx). */
export type AnnouncementBadgeVariant =
  | 'default'
  | 'secondary'
  | 'outline'
  | 'accent'
  | 'refused';

interface LabelWithVariant {
  readonly label: string;
  readonly variant: AnnouncementBadgeVariant;
}

/** 5 états d'annonce → libellé FR + variant (renfort couleur ; le libellé porte l'information). */
const ANNOUNCEMENT_STATE_LABELS: Record<AnnouncementStateDto, LabelWithVariant> = {
  Brouillon: { label: 'Brouillon', variant: 'outline' },
  Planifiee: { label: 'Planifiée', variant: 'secondary' },
  Modifiable: { label: 'Modifiable', variant: 'accent' },
  Verrouillee: { label: 'Verrouillée', variant: 'default' },
  Annulee: { label: 'Annulée', variant: 'refused' },
};

/** Libellé FR + variant d'un état d'annonce. */
export function announcementStateLabel(
  state: AnnouncementStateDto,
): LabelWithVariant {
  return ANNOUNCEMENT_STATE_LABELS[state];
}

/** États sélectionnables au filtre du registre (ordre d'affichage). */
export const ANNOUNCEMENT_STATES: readonly AnnouncementStateDto[] = [
  'Brouillon',
  'Planifiee',
  'Modifiable',
  'Verrouillee',
  'Annulee',
];

/** Type guard : la valeur est-elle un état d'annonce connu ? (validation search param). */
export function isAnnouncementState(
  value: unknown,
): value is AnnouncementStateDto {
  return (
    typeof value === 'string' &&
    (ANNOUNCEMENT_STATES as readonly string[]).includes(value)
  );
}

/**
 * État d'AFFICHAGE effectif (lazy read-side, ARCH GATE-7 option A) : une annonce stockée `Modifiable` dont
 * la fenêtre de modification est écoulée (`now > modificationWindowExpiresAt`) est présentée `Verrouillee`
 * SANS dépendre d'un sweep serveur. Les autres états sont rendus tels quels. Pur, sans effet de bord.
 */
export function effectiveAnnouncementState(
  announcement: Pick<AnnouncementDto, 'state' | 'modificationWindowExpiresAt'>,
  now: Date,
): AnnouncementStateDto {
  if (
    announcement.state === 'Modifiable' &&
    announcement.modificationWindowExpiresAt !== null &&
    now.getTime() > new Date(announcement.modificationWindowExpiresAt).getTime()
  ) {
    return 'Verrouillee';
  }
  return announcement.state;
}

/** La fenêtre de modification est-elle ouverte (édition inline autorisée) ? */
export function isModificationWindowOpen(
  announcement: Pick<AnnouncementDto, 'state' | 'modificationWindowExpiresAt'>,
  now: Date,
): boolean {
  return effectiveAnnouncementState(announcement, now) === 'Modifiable';
}
